import { useRef, useEffect } from 'react';
import { Transformer, Group } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import Konva from 'konva';
import CustomRasterImage from '../../Raster/CustomRasterImg';
import { removeUploadingMap, editMap, setEditingMap, setUploadProgress, setBaselineMap } from 'store/slices/CustomMapSlice';
import { selectCustomMap } from 'store/selectors/customMapSelector';
import { KonvaGroupNames } from 'components/tool/utils';
import { RootState } from 'store';
import { useUpdateCustomMap } from 'hooks/api/customMap/useUpdateCustomMap';

export default function CustomMapGroup() {
	const { customMaps, editingMapId, isCalibrating, selectedMap, uploadingMapIds } = useSelector(selectCustomMap);
	const dispatch = useDispatch();
	const { groundMetersPerPixel, uuid } = useSelector((state: RootState) => state.roofData.data);
	const { multiFingersPress } = useSelector((state: RootState) => state.toolNewPostions.data);

	const trRef = useRef<Konva.Transformer | null>(null);
	const shapeRefs = useRef<Record<number, Konva.Group | null>>({});

	const { mutateAsync: updateCustomMap } = useUpdateCustomMap(uuid);

	useEffect(() => {
		if (!trRef.current) return;

		if (!selectedMap) {
			trRef.current.nodes([]);
			return;
		}

		const node = shapeRefs.current[selectedMap.id];
		// ensure image inside group is loaded
		const imageNode = node?.findOne('Image') as Konva.Image;
		if (node && node.isVisible() && imageNode?.image()) {
			trRef.current.nodes([node]);
		} else {
			trRef.current.nodes([]);
		}

		trRef.current.getLayer()?.batchDraw();
	}, [selectedMap]);

	function handleTransformEnd(e: Konva.KonvaEventObject<Event>, id: number) {
		const node = shapeRefs.current[id];
		if (!node || !selectedMap) return;

		const scaleX = node.scaleX();
		const scaleY = node.scaleY();

		const stage = node.getStage();

		if (!stage) return;

		let rotation = node.rotation() % 360;
		if (rotation < 0) rotation += 360;

		// A resize with a centered offset + non-centered (corner) scaling always shifts x/y too,
		// to keep the opposite corner anchored on screen — Konva does this automatically on the
		// node, but it was never read back here. Without it, the image snaps back to its old
		// (pre-resize) center as soon as Redux re-renders it at the stale x/y, and the backend
		// PATCH below never gets the corrected position either.
		const editedState: Partial<DesignCustomMap> = {
			x: node.x(),
			y: node.y(),
			rotation: Math.floor(rotation),
			scaleX,
			scaleY,
			metersPerPixel: groundMetersPerPixel * scaleX
		};

		dispatch(editMap({ id: selectedMap.id, editedState }));

		updateEditedState(editedState);
	}

	function handleImageLoad(id: number) {
		if (uploadingMapIds.includes(id)) {
			dispatch(removeUploadingMap(id));
			dispatch(setEditingMap(id));
		}
	}

	function handleDragEnd(evt: Konva.KonvaEventObject<DragEvent>) {
		evt.cancelBubble = true;
		if (!selectedMap) return;
		const node = evt.currentTarget;
		const stage = node.getStage();
		if (stage) stage.container().style.cursor = 'default';

		const nextX = node.x();
		const nextY = node.y();

		dispatch(editMap({ id: selectedMap.id, editedState: { x: nextX, y: nextY } }));
		updateEditedState({ x: nextX, y: nextY });
	}

	async function updateEditedState(payload: Partial<DesignCustomMap>) {
		if (!selectedMap) return;
		const updatedMap = await updateCustomMap({ id: selectedMap.id, payload });
		dispatch(setBaselineMap(updatedMap));
	}

	return (
		<>
			<Group name={KonvaGroupNames.customMapGroup}>
				{customMaps.map((customMap: DesignCustomMap) => (
					<CustomRasterImage
						key={customMap.id}
						ref={(node) => {
							shapeRefs.current[customMap.id] = node;
						}}
						customMap={customMap}
						isSelected={editingMapId === customMap.id}
						onTransformEnd={(e) => handleTransformEnd(e, customMap.id)}
						isCalibrating={isCalibrating}
						onImageLoad={handleImageLoad}
						onDragEnd={handleDragEnd}
						isDraggable={!multiFingersPress}
					// onPointerDown={handlePointerDown}
					// onPointerMove={handlePointerMove}
					/>
				))}
			</Group>

			<Transformer
				ref={trRef}
				rotateLineVisible={true}
				anchorSize={15}
				anchorFill='#ddedfe'
				anchorStroke='#fff'
				anchorCornerRadius={2}
				borderStroke="rgba(255, 255, 255, 1)"
				borderDash={[10, 2]}
				borderStrokeWidth={3}
				flipEnabled={false}
				keepRatio={true}
				ignoreStroke={true}
				enabledAnchors={[
					'top-left',
					'top-right',
					'bottom-left',
					'bottom-right'
				]}
			/>
		</>
	);
}
